import debounce from 'lodash/debounce';
import defer from 'lodash/defer';
import isNumber from 'lodash/isNumber';
import { useCallback, useState, useEffect, useMemo } from 'react';

import type { RefObject } from 'react';

interface Options {
  wait?: number;
  offset?: number;
}

export const useIsScrolled = <Ref extends RefObject<HTMLElement>>(
  ref: Ref,
  { wait = 50, offset = 0 }: Options = {},
) => {
  const [isScrolled, setIsScrolled] = useState(false);

  const onScroll = useCallback(() => {
    const scrolledHeight = ref.current?.scrollTop;

    if (isNumber(scrolledHeight)) {
      setIsScrolled(offset < scrolledHeight);
    }
  }, [ref, offset]);

  const onScrollDebounce = useMemo(
    () => debounce(onScroll, wait),
    [onScroll, wait],
  );

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return undefined;
    }

    defer(onScroll);

    element.addEventListener('scroll', onScrollDebounce);

    return () => {
      element.removeEventListener('scroll', onScrollDebounce);
    };
  }, [onScroll, onScrollDebounce, ref]);

  return isScrolled;
};
